import type { AuraDimension, AuraType, Question } from '../data/dayVibesQuestions';
import type { DailyDayVibeQuestion, DayVibeTrait } from './dayVibes';

// Turns a Firestore day-vibe question into the screen/scoring shape used by
// DayVibesQuestionScreen and useDayVibes.submitAnswers' aura calculation.
// Mirrored on the server by functions/dayVibeGame.js (the web Vibe game) —
// utils/__tests__/dayVibeGameParity.test.ts fails if the two drift apart.

const TRAIT_AURA_WEIGHTS: Record<DayVibeTrait, Partial<Record<AuraType, number>>> = {
  social: { SOFT_CHAOS: 2, SOLAR_BURST: 2, CITY_SOUL: 1 },
  calm: { GOLDEN_HOUR: 2, QUIET_STORM: 2 },
  explorer: { CITY_SOUL: 2, NEON_WOLF: 1 },
  chaos: { SOFT_CHAOS: 2, NEON_WOLF: 1 },
  creative: { QUIET_STORM: 2, CITY_SOUL: 1 },
  deep: { QUIET_STORM: 2, CITY_SOUL: 1 },
  energetic: { SOLAR_BURST: 2, NEON_WOLF: 2 },
  thoughtful: { QUIET_STORM: 2, GOLDEN_HOUR: 1 },
  aesthetic: { CITY_SOUL: 2, GOLDEN_HOUR: 1 },
  grounded: { GOLDEN_HOUR: 2, QUIET_STORM: 1 },
  spontaneous: { SOFT_CHAOS: 2, SOLAR_BURST: 1 },
  nostalgic: { GOLDEN_HOUR: 2, QUIET_STORM: 1 },
  emotionally_open: { SOLAR_BURST: 2, SOFT_CHAOS: 1 },
  observant: { CITY_SOUL: 2, QUIET_STORM: 1 },
  adventurous: { NEON_WOLF: 2, SOLAR_BURST: 1 },
};

const TRAIT_DIMENSION_WEIGHTS: Record<DayVibeTrait, Partial<Record<AuraDimension, number>>> = {
  social: { social: 3 },
  calm: { chill: 3, focus: 1 },
  explorer: { adventure: 3, social: 1 },
  chaos: { spontaneous: 3, nightlife: 1 },
  creative: { creative: 3, focus: 1 },
  deep: { focus: 2, creative: 1, chill: 1 },
  energetic: { social: 2, nightlife: 1, adventure: 1 },
  thoughtful: { focus: 3, chill: 1 },
  aesthetic: { creative: 2, romantic: 1 },
  grounded: { chill: 3, focus: 1 },
  spontaneous: { spontaneous: 3, adventure: 1 },
  nostalgic: { romantic: 2, chill: 1 },
  emotionally_open: { romantic: 2, social: 2 },
  observant: { focus: 2, creative: 1 },
  adventurous: { adventure: 3, nightlife: 1 },
};

export function pickAurasForOption(question: DailyDayVibeQuestion, option: string): AuraType[] {
  const scores: Record<AuraType, number> = {
    CITY_SOUL: 0,
    SOFT_CHAOS: 0,
    GOLDEN_HOUR: 0,
    NEON_WOLF: 0,
    QUIET_STORM: 0,
    SOLAR_BURST: 0,
  };
  const weighted = question.optionTraitWeights?.find(entry => entry.option === option)?.weights;
  const traits = weighted
    ? Object.entries(weighted).map(([trait, weight]) => ({ trait: trait as DayVibeTrait, weight: Number(weight) || 0 }))
    : (question.optionTraits?.find(entry => entry.option === option)?.traits ?? question.weightTags ?? [])
        .map((trait, index) => ({ trait, weight: Math.max(1, 3 - index) }));

  traits.forEach(({ trait, weight }) => {
    Object.entries(TRAIT_AURA_WEIGHTS[trait]).forEach(([aura, auraWeight]) => {
      scores[aura as AuraType] += weight * (auraWeight ?? 0);
    });
  });

  return (Object.entries(scores) as [AuraType, number][])
    .sort((left, right) => right[1] - left[1])
    .slice(0, 2)
    .map(([aura]) => aura);
}

export function pickScoresForOption(
  question: DailyDayVibeQuestion,
  option: string,
): Partial<Record<AuraDimension, number>> {
  const scores: Partial<Record<AuraDimension, number>> = {};
  const weighted = question.optionTraitWeights?.find(entry => entry.option === option)?.weights;
  const traits = weighted
    ? Object.entries(weighted).map(([trait, weight]) => ({ trait: trait as DayVibeTrait, weight: Number(weight) || 0 }))
    : (question.optionTraits?.find(entry => entry.option === option)?.traits ?? question.weightTags ?? [])
        .map((trait, index) => ({ trait, weight: Math.max(1, 3 - index) }));

  traits.forEach(({ trait, weight }) => {
    Object.entries(TRAIT_DIMENSION_WEIGHTS[trait]).forEach(([dimension, dimensionWeight]) => {
      scores[dimension as AuraDimension] = (scores[dimension as AuraDimension] ?? 0) + weight * (dimensionWeight ?? 0);
    });
  });

  return scores;
}

export function toScreenQuestion(question: DailyDayVibeQuestion, t: (k: string) => string): Question {
  return {
    id: question.id,
    text: question.question,
    hint: question.allowMultiSelect ? t('vibes.pickAllHint') : t('vibes.dontOverthink'),
    type: question.allowMultiSelect ? 'multi' : 'single',
    maxSelections: question.allowMultiSelect ? Math.min(3, Math.max(1, question.visibleOptions.length)) : undefined,
    options: question.visibleOptions.map(option => ({
      id: option,
      text: option,
      auras: pickAurasForOption(question, option),
      scores: pickScoresForOption(question, option),
    })),
  };
}
